import {
  Controller,
  FieldPath,
  FieldValues,
  RegisterOptions,
  useFormContext,
} from "react-hook-form";

import { FilterableSinglePicker } from "src/components/Pickers/FilterableSinglePicker";
import { useCreateError } from "src/errors";
import { useGetOperatorsQuery } from "src/graphql/anti-fraud/queries/rules/OperatorsQuery.ts";
import { useGetOperatorLabel } from "src/hooks/useGetOperatorLabel";
import { mapToApiDataType } from "src/mappers/typedValue.mappers";
import { Operator } from "src/types/operator";
import { AppDataType } from "src/types/typedValue";

export type OperatorsFilterableSinglePickerFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  id: string;
  label?: string;
  helperText?: string;
  placeholder?: string;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  name: TName;
  dataType?: AppDataType | null;
};

export function OperatorFilterableSinglePickerFormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  rules,
  helperText,
  label,
  placeholder,
  dataType,
}: OperatorsFilterableSinglePickerFormFieldProps<TFieldValues, TName>) {
  const { setError } = useFormContext<TFieldValues>();
  const createError = useCreateError();
  const apiDataType = dataType ? mapToApiDataType(dataType) : undefined;

  const { data, error, loading } = useGetOperatorsQuery({
    variables: { dataType: apiDataType ?? "String" },
    skip: !apiDataType,
    onError: () => {
      const error = createError("FETCH-OPERATORS-ERROR", null);
      setError(name, { message: error.message });
    },
  });

  const getOperatorLabel = useGetOperatorLabel();

  return (
    <Controller
      name={name}
      rules={rules}
      render={({ field, fieldState: { error: fieldError } }) => {
        return (
          <FilterableSinglePicker<Operator>
            options={data || []}
            {...field}
            getOptionValue={(option) => option.code}
            getOptionLabel={(option) => getOperatorLabel(option.code)}
            invalid={!!fieldError}
            invalidText={fieldError?.message}
            label={label}
            helperText={helperText}
            loading={loading}
            loadingOptionsError={!!error}
            placeholder={placeholder}
            readOnly={!dataType}
          />
        );
      }}
    />
  );
}
